import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './config/env';
import './index.css';
import LocalStorageService from './services/localStorageService';

const RUNTIME_ERROR_BUFFER_KEY = 'bn_runtime_error_buffer_v1';
const RUNTIME_ERROR_BUFFER_LIMIT = 20;

const readRuntimeErrorBuffer = () => {
    try {
        const raw = localStorage.getItem(RUNTIME_ERROR_BUFFER_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeRuntimeErrorBuffer = (entries) => {
    try {
        localStorage.setItem(
            RUNTIME_ERROR_BUFFER_KEY,
            JSON.stringify(entries.slice(-RUNTIME_ERROR_BUFFER_LIMIT))
        );
    } catch {
        // Ignore storage write failures (private mode/quota).
    }
};

const recordRuntimeError = (kind, payload) => {
    const entry = {
        kind,
        at: new Date().toISOString(),
        href: window.location.href,
        userAgent: navigator.userAgent,
        payload,
    };
    const next = [...readRuntimeErrorBuffer(), entry];
    writeRuntimeErrorBuffer(next);
};

const reportBufferedRuntimeErrors = () => {
    const entries = readRuntimeErrorBuffer();
    if (!entries.length) return;

    console.groupCollapsed(
        `Recovered ${entries.length} runtime error${entries.length > 1 ? 's' : ''} from previous page lifecycle`
    );
    entries.forEach((entry, idx) => {
        console.error(`[Recovered ${idx + 1}] ${entry.kind}`, entry);
    });
    console.groupEnd();
};

// Polyfill for Crypto.randomUUID (Safari iOS doesn't support it)
if (!crypto.randomUUID) {
    crypto.randomUUID = function () {
        return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
            (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
        );
    };
}

// Flush pending localStorage writes before page unload
window.addEventListener('beforeunload', () => {
    LocalStorageService.flushWrites();
});

// Also flush on visibility change (when switching tabs/apps)
window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
        LocalStorageService.flushWrites();
    }
});

// Global error handler - catch all unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
    const payload = {
        reason: event.reason,
        promise: event.promise,
        message: event.reason?.message,
        stack: event.reason?.stack,
        fullError: event.reason
    };
    recordRuntimeError('unhandledrejection', payload);
    console.error('🔴 Unhandled Promise Rejection:', payload);

    return true;
});

// Global error handler - catch all runtime errors
window.addEventListener('error', (event) => {
    const payload = {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        stack: event.error?.stack
    };
    recordRuntimeError('error', payload);
    console.error('🔴 Global Error:', payload);

    return true;
});

// Override console.error to prevent React error overlay in development
const originalConsoleError = console.error;
console.error = (...args) => {
    // Still log to console for debugging
    originalConsoleError.apply(console, args);

    // Prevent React error overlay from showing
    if (args[0]?.includes?.('uncaught') || args[0]?.includes?.('Uncaught')) {
        return;
    }
};

// Create QueryClient outside of render to ensure it's stable
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
            onError: (error) => {
                console.error('React Query Error:', error);
            }
        },
    },
});

// Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Error Boundary caught:', error, errorInfo);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (this.state.hasError) {
            console.error('App error:', this.state.error);
            return (
                <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-gray-100 flex items-center justify-center p-6">
                    <div className="max-w-md w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
                        <h1 className="text-lg font-semibold mb-2">Something went wrong</h1>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                            The app hit an unexpected error. Please retry. If the issue continues, refresh the page.
                        </p>
                        <button
                            type="button"
                            onClick={this.handleRetry}
                            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
                        >
                            Try again
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <App />
                    <Toaster
                        position="top-right"
                        toastOptions={{
                            duration: 4000,
                            style: {
                                background: '#363636',
                                color: '#fff',
                            },
                        }}
                    />
                </BrowserRouter>
            </QueryClientProvider>
        </ErrorBoundary>
    </React.StrictMode>
); 

// Print any previously buffered runtime failures after app boot.
reportBufferedRuntimeErrors();